/**
 * First-party usage tracking: queue events, flush via fetch keepalive / sendBeacon,
 * and replay unsent batches from localStorage. Never records form field values.
 */
import PlatformAPI from '../api/platform'
import { resolveUsageControl, usageEventLabel } from './usageEventLabel'

const STORAGE_KEY = 'myco_usage_queue_v1'
const SESSION_KEY = 'myco_usage_session_id'
const FLUSH_MS = 4000
const MAX_QUEUE = 80

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `s-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function loadQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveQueue(events) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_QUEUE)))
  } catch {
    /* ignore quota */
  }
}

function sessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = uuid()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return uuid()
  }
}

/** Current browser usage session id (also used by error reports). */
export function getUsageSessionId() {
  return sessionId()
}

/** Start a fresh browser usage session (e.g. after login/logout user switch). */
export function resetUsageSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    /* ignore */
  }
}

let queue = loadQueue()
let flushTimer = null
let started = false
let routerUnhook = null

function enqueue(event) {
  queue.push({
    session_id: sessionId(),
    ts: Date.now(),
    ...event,
  })
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE)
  saveQueue(queue)
  scheduleFlush()
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush()
  }, FLUSH_MS)
}

async function flush({ keepalive = false } = {}) {
  if (!queue.length) return
  const batch = queue.splice(0, MAX_QUEUE)
  saveQueue(queue)
  const body = JSON.stringify({ events: batch })
  try {
    if (keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      const ok = navigator.sendBeacon('/api/v1/platform/v1/usage/events', blob)
      if (!ok) {
        queue = batch.concat(queue)
        saveQueue(queue)
      }
      return
    }
    await PlatformAPI.usage.ingest({ events: batch })
  } catch {
    queue = batch.concat(queue).slice(-MAX_QUEUE)
    saveQueue(queue)
  }
}

function clickMeta(ev) {
  const target = ev.target instanceof Element ? ev.target : null
  const control = resolveUsageControl(target)
  const w = window.innerWidth || 1
  const h = window.innerHeight || 1
  const label = usageEventLabel(target)
  return {
    x_pct: Math.round(((ev.clientX || 0) / w) * 1000) / 10,
    y_pct: Math.round(((ev.clientY || 0) / h) * 1000) / 10,
    label,
    tag: control?.tagName?.toLowerCase?.() || target?.tagName?.toLowerCase?.() || '',
    testid: control?.getAttribute?.('data-testid') || target?.getAttribute?.('data-testid') || '',
    role: control?.getAttribute?.('role') || target?.getAttribute?.('role') || '',
    width: w,
    height: h,
    scroll_y: window.scrollY || 0,
  }
}

function onClick(ev) {
  const label = usageEventLabel(ev.target instanceof Element ? ev.target : null)
  // Ignore empty page chrome clicks that resolve to nothing useful.
  if (!label) return
  enqueue({
    event_type: 'click',
    path: window.location.pathname + window.location.search,
    name: label,
    meta: clickMeta(ev),
  })
}

function onFocusIn(ev) {
  const el = ev.target
  if (!el || !/^(INPUT|TEXTAREA|SELECT)$/i.test(el.tagName)) return
  const label = usageEventLabel(el) || el.getAttribute('name') || el.getAttribute('placeholder') || el.tagName.toLowerCase()
  enqueue({
    event_type: 'input',
    path: window.location.pathname + window.location.search,
    name: label,
    meta: {
      label,
      tag: el.tagName.toLowerCase(),
      testid: el.getAttribute('data-testid') || '',
    },
  })
}

function onUnload() {
  flush({ keepalive: true })
}

/**
 * @param {{ router: import('vue-router').Router, store: import('vuex').Store }} opts
 */
export function startUsageTracking({ router, store }) {
  if (started || typeof window === 'undefined') return
  started = true

  // Replay any queued events from a previous session.
  scheduleFlush()

  window.addEventListener('click', onClick, true)
  window.addEventListener('focusin', onFocusIn, true)
  window.addEventListener('pagehide', onUnload)
  window.addEventListener('beforeunload', onUnload)

  // sessionStorage session_id survives logout→login as another user; rotate it
  // when the authenticated user identity changes so sessions do not glue together.
  if (store && typeof store.watch === 'function') {
    let lastUserId = store.state?.user?.id ?? null
    store.watch(
      (state) => state.user?.id ?? null,
      (uid) => {
        if (uid !== lastUserId) {
          lastUserId = uid
          resetUsageSession()
        }
      }
    )
  }

  if (router) {
    routerUnhook = router.afterEach((to) => {
      if (!store?.state?.user) return
      enqueue({
        event_type: 'page_view',
        path: to.fullPath,
        name: String(to.name || to.path),
        meta: {},
      })
    })
  }
}

export function stopUsageTracking() {
  if (!started) return
  started = false
  window.removeEventListener('click', onClick, true)
  window.removeEventListener('focusin', onFocusIn, true)
  window.removeEventListener('pagehide', onUnload)
  window.removeEventListener('beforeunload', onUnload)
  if (typeof routerUnhook === 'function') routerUnhook()
  routerUnhook = null
}

export function trackAction(name, meta = {}) {
  enqueue({
    event_type: 'action',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    name,
    meta: { ...meta },
  })
}
